import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { accountQuery, ApiError, login } from '@/api';
import { safeRedirect } from '@/lib/safeRedirect';
import { LoginModal, type LoginFormValues } from '@/ui/auth';

export const Route = createFileRoute('/login')({
  // Where to return the player after signing in, set when a rejected session
  // bounces them here.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = safeRedirect(search.redirect);
    return redirect ? { redirect } : {};
  },
  component: LoginPage,
});

function loginErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isUnauthorized) {
      const body = error.body;
      if (typeof body === 'object' && body !== null) {
        const description =
          (body as Record<string, unknown>).error_description ??
          (body as Record<string, unknown>).Message ??
          (body as Record<string, unknown>).message;
        if (typeof description === 'string' && description.trim()) {
          return description;
        }
      }
      return 'Wrong username or password';
    }
    return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Wrong username or password';
}

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { data: account } = useQuery(accountQuery());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requireLoginScreen = account?.RequireLoginScreen === true;
  // Legacy strips register / forgot chrome when RequireLoginScreen is on.
  const allowRegistration =
    account?.AllowUserRegistration !== false && !requireLoginScreen;
  const allowForgotPassword = !requireLoginScreen;
  const chatEnabled = account?.Chat === true;
  const dismissible = !requireLoginScreen;

  const leave = () => {
    if (!dismissible) return;
    const target = redirect ?? '/';
    void navigate({ href: target });
  };

  const handleSubmit = async (values: LoginFormValues) => {
    if (!values.username || !values.password) {
      setError('Wrong username or password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await login(values.username, values.password, {
        persist: values.keepLoggedIn,
      });

      if (result.requiresSmsVerification) {
        setError('SMS verification is required before you can sign in.');
        return;
      }

      const target = redirect ?? '/';
      await navigate({ href: target });
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginModal
      open
      dismissible={dismissible}
      onClose={leave}
      loading={loading}
      error={error}
      allowRegistration={allowRegistration}
      allowForgotPassword={allowForgotPassword}
      chatEnabled={chatEnabled}
      onSubmit={handleSubmit}
    />
  );
}
